import React, { useEffect, useState, useCallback } from "react";
import { Key, Loader2, Search, RefreshCw, XCircle, ChevronUp, ChevronDown, ShieldCheck } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { Pagination } from "./Pagination";

// Simple helper for localizing epoch date
function formatDateTxt(epoch: string | number | undefined | null) {
    if (!epoch) return "-";
    const num = Number(epoch);
    if (isNaN(num)) return epoch;
    // Assume seconds since epoch
    const d = new Date(num * 1000);
    return d.toLocaleDateString();
}

const ExpandableText = ({ text, maxLength = 40 }: { text: string; maxLength?: number }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    if (!text || text === "-") return <span>{text || "-"}</span>;
    
    if (text.length <= maxLength) {
        return <span className="break-words">{text}</span>;
    }
    
    return (
        <div className="flex flex-col items-start min-w-0">
            <span className="break-words max-w-full">
                {isExpanded ? text : `${text.slice(0, maxLength)}...`}
            </span>
            <button 
                onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                className="text-[9px] font-bold text-yellow-600 dark:text-yellow-500 hover:text-yellow-700 dark:hover:text-yellow-400 mt-1 bg-yellow-50 dark:bg-yellow-500/10 hover:bg-yellow-100 dark:hover:bg-yellow-500/20 px-1.5 py-0.5 rounded transition-colors cursor-pointer"
            >
                {isExpanded ? 'Show Less' : 'Show More'}
            </button>
        </div>
    );
};

interface SSLProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const SSL: React.FC<SSLProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [certData, setCertData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination & Search state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1); // Reset to page 1 when search changes
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchCerts = useCallback(async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;

            // Build the query object
            const queryObject: any = {
                client_id,
                organization_id,
                agent_id,
                asset_id,
            };

            // Add search parameter if it exists
            if (debouncedSearch.trim() !== "") {
                queryObject.$or = [{ common_name: { $ilike: `%${debouncedSearch}%` } }, { issuer: { $ilike: `%${debouncedSearch}%` } }, { key_algorithm: { $ilike: `%${debouncedSearch}%` } }, { key_usage: { $ilike: `%${debouncedSearch}%` } }];
            }

            const params: any = {
                limit,
                skip,
                query: JSON.stringify(queryObject),
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/asset_certificates", { params });

            if (response.data.status) {
                setCertData(response.data.data ?? []);
                setTotalRecords(response.data.total ?? 0);
            } else {
                setCertData([]);
                setTotalRecords(0);
            }
        } catch (err: any) {
            showError(err.message);
            setCertData([]);
            setTotalRecords(0);
        }
        setLoading(false);
    }, [client_id, organization_id, agent_id, asset_id, currentPage, limit, debouncedSearch, sortConfig]);

    useEffect(() => {
        fetchCerts();
    }, [fetchCerts]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, icon }: { label: string; sortKey: string, icon?: React.ReactNode }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group select-none hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
            >
                <div className="flex items-center gap-1.5">
                    {icon}
                    {label}
                    <div className="flex items-center ml-1">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-yellow-600 dark:text-yellow-400" />
                            ) : (
                                <ChevronDown size={12} className="text-yellow-600 dark:text-yellow-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-slate-400 dark:text-gray-200 group-hover:text-slate-600 dark:group-hover:text-white" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    // Calculate total pages for the Pagination component
    const totalPages = Math.max(1, Math.ceil(totalRecords / limit));

    return (
        <div className="p-3 h-full flex flex-col">
            {/* Header with Search and Refresh */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 border-b border-slate-200 dark:border-gray-700 pb-3 shrink-0">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <ShieldCheck className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">SSL Certificates</h2>
                        <p className="text-slate-500 dark:text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {totalRecords || '...'} found
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchCerts}
                        disabled={loading}
                        className="p-2 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-slate-400 dark:text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search certificates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-64 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-8 shadow-sm"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-gray-400 dark:hover:text-white transition-colors"
                            >
                                <XCircle className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-hidden flex flex-col bg-white dark:bg-gray-900/40 border border-slate-200 dark:border-gray-700/50 rounded-md shadow-sm">
                <div className="overflow-x-auto custom-scroll flex-1">
                    <table className="w-full text-left text-xs border-collapse min-w-[800px]">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <SortableHeader label="Common Name" sortKey="common_name" />
                                <SortableHeader label="Issuer" sortKey="issuer" />
                                <SortableHeader label="Path" sortKey="path" />
                                <SortableHeader label="Algorithm" sortKey="key_algorithm" />
                                <SortableHeader label="Usage" sortKey="key_usage" />
                                <SortableHeader label="Self Signed" sortKey="self_signed" />
                                <SortableHeader label="Valid From" sortKey="not_valid_before" />
                                <SortableHeader label="Valid Until" sortKey="not_valid_after" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/60 bg-white dark:bg-gray-900/20 text-xs">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500 dark:text-gray-200">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-yellow-500" />
                                        Loading certificates...
                                    </td>
                                </tr>
                            ) : certData.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500 dark:text-gray-200">
                                        <Key className="w-6 h-6 text-slate-400 dark:text-gray-700 mx-auto mb-2" />
                                        No Certificates Found
                                    </td>
                                </tr>
                            ) : (
                                certData.map((cert) => (
                                    <tr key={cert.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors">
                                        <td className="px-3 py-2 text-[11px] font-semibold text-slate-900 dark:text-gray-200 min-w-[150px] align-top" title={cert.common_name}>
                                            <ExpandableText text={cert.common_name || "-"} maxLength={35} />
                                        </td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-300 min-w-[200px] align-top" title={cert.issuer}>
                                            <ExpandableText text={cert.issuer || "-"} maxLength={45} />
                                        </td>
                                        <td className="px-3 py-2 text-[10px] text-slate-600 dark:text-gray-300 min-w-[150px] align-top" title={cert.path}>
                                            <ExpandableText text={cert.path || "-"} maxLength={35} />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap align-top">
                                            <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-[9px] font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">
                                                {cert.key_algorithm || "-"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-[10px] text-slate-700 dark:text-gray-200 min-w-[150px] align-top" title={cert.key_usage}>
                                            <ExpandableText text={cert.key_usage || "-"} maxLength={30} />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap align-top">
                                            {String(cert.self_signed) === "1" || cert.self_signed === 1 || cert.self_signed === true ? (
                                                <span className="px-1.5 py-0.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded text-[9px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                                                    true
                                                </span>
                                            ) : String(cert.self_signed) === "0" || cert.self_signed === 0 || cert.self_signed === false ? (
                                                <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-[9px] font-bold text-slate-600 dark:text-gray-400 uppercase tracking-wider">
                                                    false
                                                </span>
                                            ) : (
                                                <span className="text-slate-400 dark:text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-[10px] text-slate-700 dark:text-white font-mono align-top">
                                            {formatDateTxt(cert.not_valid_before)}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-[10px] text-slate-700 dark:text-white font-mono align-top">
                                            {formatDateTxt(cert.not_valid_after)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Integration of the Pagination Component */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={limit}
                    totalItems={totalRecords}
                    onItemsPerPageChange={(n) => {
                        setLimit(n);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
};

export default SSL;